import { useState, useEffect, useRef } from 'react'
import audioFile from './assets/audio/CoffeeCoded.mp3'

export function AudioButton() {
  const [isMuted, setIsMuted] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileLandscape, setIsMobileLandscape] = useState(false)
  const canvasRef = useRef(null)
  const audioRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const sourceRef = useRef(null)
  const animationRef = useRef(null)
  const progressRef = useRef(0)
  const yMultiplierRef = useRef(0)
  const timeRef = useRef(0)

  const width = 48
  const height = 24

  // Check for mobile/landscape - update on resize
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth
      const height = window.innerHeight
      // Better mobile detection including Galaxy devices
      const mobile = width <= 768 || ('ontouchstart' in window)
      const landscape = mobile && width > height
      setIsMobile(mobile)
      setIsMobileLandscape(landscape)
    }

    // Initial check
    checkMobile()

    // Update on any resize or orientation change
    const handleResize = () => {
      checkMobile()
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    // Also listen for viewport changes (for dev tools, etc)
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(document.documentElement)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      resizeObserver.disconnect()
    }
  }, [])

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(audioFile)
    audioRef.current.loop = true
    audioRef.current.volume = 0.3

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Initialize Web Audio API for frequency analysis
  const initAudioContext = () => {
    if (!audioContextRef.current && audioRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      audioContextRef.current = new AudioContext()
      analyserRef.current = audioContextRef.current.createAnalyser()
      analyserRef.current.fftSize = 128

      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current)
      sourceRef.current.connect(analyserRef.current)
      analyserRef.current.connect(audioContextRef.current.destination)
    }
  }

  // Initialize canvas and start animation
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const context = canvas.getContext('2d')
    const dpr = 2

    canvas.width = width * dpr
    canvas.height = height * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    context.scale(dpr, dpr)

    context.lineWidth = 1.5
    context.strokeStyle = '#ffffff'

    const animate = () => {
      timeRef.current += 0.05

      const canvasWidth = width + 2
      const canvasHeight = height / 2
      const progress = canvasWidth * progressRef.current
      const baseIncrease = 90 / 180 * Math.PI / (canvasHeight / 2)

      context.clearRect(0, 0, canvas.width, canvas.height)
      context.beginPath()

      let counter = 0
      let x = 0
      let y = canvasHeight

      // Get frequency data if audio is playing
      let frequencyModifier = 1
      if (analyserRef.current && !isMuted && hasInteracted) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
        analyserRef.current.getByteFrequencyData(dataArray)
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length
        frequencyModifier = 0.5 + (average / 128)
      }

      for (let i = -4; i < canvasWidth; i++) {
        if (progress >= i) {
          context.moveTo(x, y)

          x = i

          if (!isMuted && yMultiplierRef.current > 0 && hasInteracted) {
            const wave1 = Math.sin(counter + timeRef.current * 2) * 0.4
            const wave2 = Math.sin(counter * 1.5 + timeRef.current * 1.5) * 0.3
            const wave3 = Math.sin(counter * 0.8 + timeRef.current * 2.5) * 0.2
            const wave4 = Math.sin(counter * 2.2 + timeRef.current * 1.8) * 0.1

            const combinedWave = (wave1 + wave2 + wave3 + wave4) * frequencyModifier

            y = canvasHeight - combinedWave * (canvasHeight - 1) * yMultiplierRef.current
          } else {
            y = canvasHeight - Math.sin(counter) * (canvasHeight - 1) * yMultiplierRef.current
          }

          counter += baseIncrease

          context.lineTo(x, y)
        }
      }

      context.stroke()
      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isMuted, hasInteracted])

  // Animate in on mount
  useEffect(() => {
    let startTime = Date.now()
    const duration = 1000

    const animateIn = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      progressRef.current = easeOutExpo(progress)

      if (progress < 1) {
        requestAnimationFrame(animateIn)
      }
    }

    animateIn()
  }, [])

  // Update yMultiplier when mute state changes
  useEffect(() => {
    const targetY = isMuted ? 0 : (isHovered ? 0.8 : 1)
    animateYMultiplier(targetY, 300)
  }, [isMuted, isHovered])

  const easeOutExpo = (t) => {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
  }

  const easeInOutCubic = (t) => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  }

  const animateYMultiplier = (target, duration) => {
    const start = yMultiplierRef.current
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      yMultiplierRef.current = start + (target - start) * easeInOutCubic(progress)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    animate()
  }

  const handleClick = async () => {
    if (!hasInteracted) {
      setHasInteracted(true)
      initAudioContext()
    }

    if (audioRef.current) {
      if (isMuted) {
        try {
          if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume()
          }
          await audioRef.current.play()
          setIsMuted(false)
        } catch (error) {
          console.error('Error playing audio:', error)
        }
      } else {
        audioRef.current.pause()
        setIsMuted(true)
      }
    }
  }

  return (
    <button
      style={{
        position: 'fixed',
        // EXACT same positioning as "Our Projects" button
        bottom: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
        left: isMobileLandscape ? 8 : (isMobile ? 15 : 40),
        // EXACT same styling as "Our Projects" button but transparent
        fontFamily: 'Gohu, monospace',
        fontSize: isMobileLandscape ? '10px' : (isMobile ? '10px' : '14px'),
        color: 'transparent',
        background: 'transparent',
        border: '1px solid transparent', // Keep border for sizing but make transparent
        padding: isMobileLandscape ? '5px 9px' : (isMobile ? '5px 10px' : '8px 16px'),
        cursor: 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        zIndex: 1000,
        pointerEvents: 'auto',
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '80px' // Wider to accommodate larger waveform
      }}
      data-audio-button
      data-is-muted={isMuted.toString()}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Invisible text to match button dimensions */}
      <span style={{ opacity: 0, pointerEvents: 'none' }}>Our Projects</span>

      {/* Audio waveform overlay */}
      <div
        style={{
          position: 'absolute',
          left: 0, // Start at the very edge of the button
          top: '50%',
          transform: 'translateY(-50%)',
          width: width, // Use full size now that base is larger
          height: height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            filter: !isMuted && hasInteracted ? 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))' : 'none',
            display: 'block'
          }}
        />
      </div>

      {isHovered && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '2px',
            marginBottom: '8px',
            padding: '4px 8px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#ffffff',
            fontSize: '11px',
            fontFamily: 'Gohu, monospace',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 1001
          }}
        >
          {!hasInteracted ? 'Play Audio' : (isMuted ? 'Unmute' : 'Mute')}
        </div>
      )}
    </button>
  )
}

// Export a function to only play audio (never pause)
export function playAudioOnly() {
  const audioButton = document.querySelector('[data-audio-button]')
  if (audioButton) {
    // Check if audio is currently muted by looking at the data attribute
    const isMuted = audioButton.getAttribute('data-is-muted') === 'true'
    if (isMuted) {
      audioButton.click()
    }
  }
}

// Export alias for compatibility
export const triggerAudio = playAudioOnly
